import 'whatwg-fetch'
import isObject from 'lodash/isObject'

import ServerError from '../lib/error/errors/ServerError'
import ConflictError from '../lib/error/errors/ConflictError'

/**
 * Router api class with functions for communication with the backend.
 *
 * All router functions are available through the context.api.router object.
 *
 * @class
 * @alias api.router
 */
class Router {
    /** @hideconstructor */
    constructor(api) {
        this.api = api
        this.etags = {}
    }

    /**
     * Creates a querystring from an object, starting with a ?
     * If a string is passed as parameter i will just be returned
     *
     * @note If parameters contains 'headers' it will not be added to the querystring
     *
     * @param {object} parameters
     * @returns {string}
     * @private
     */
    getQuerystringFromParameters(parameters) {
        if (!parameters) return ''
        if (!isObject(parameters)) return parameters

        if (isObject(parameters)) {
            let query = []

            for(const name in parameters) {
                if (name !== 'headers' && name !== 'body') { // Dont add the headers key to the querystring
                    query.push(name + '=' + encodeURIComponent(parameters[name]))
                }
            }

            return query.length === 0 ? '' : '?' + query.join('&')
        }

        throw new Error('Could not convert parameters of type', typeof parameters)
    }


    /**
     *
     * @param {string} method - GET, POST, PUT, DELETE, HEAD
     * @param {object} parameters Object containing a headers-property
     * @returns {{method: string}}
     * @private
     */
    getRequestPropertiesForMethod(method, parameters) {

        let requestProperties = {
            method: method.toUpperCase()
        }

        // If headers is sent pass them
        if (parameters && parameters.headers) {
            requestProperties['headers'] = parameters.headers
        }

        // Add a body if there is one provided
        if (parameters && parameters.body) {
            requestProperties['body'] = parameters.body
        }

        if (!parameters || !parameters.credentials) {
            requestProperties['credentials'] = 'same-origin'
        }

        return requestProperties
    }

    /**
     * Post a binary file object to the backend
     *
     * @param {string} path
     * @param {object} file File object from file input or file drop
     * @param {Function} onProgress Callback function for progress event
     * @param {Function} onLoad Callback function for onload event
     */
    postBinary(path, file, onProgress, onLoad, onError, params) {
        var xhr = new XMLHttpRequest() //jshint ignore:line
        xhr.timeout = 180000

        xhr.addEventListener('progress', onProgress)
        xhr.addEventListener('load', onLoad)
        xhr.addEventListener('error', onError)
        xhr.addEventListener('timeout', onError)

        xhr.open('POST', path, true)

        xhr.setRequestHeader('x-infomaker-type', params.imType)
        xhr.setRequestHeader('content-type', file.type)
        xhr.setRequestHeader('x-filename', encodeURIComponent(file.name))
        xhr.send(file)
    }

    /**
     * Post data to specified backend endpoint
     *
     * @param {string} path
     * @param {object} parameters
     *
     * @returns {Promise<Response>}
     */
    post(path, parameters) {
        let url = this.getEndpoint() + path + this.getQuerystringFromParameters(parameters)
        let requestProperties = this.getRequestPropertiesForMethod('POST', parameters)
        return fetch(url, requestProperties)

    }

    /**
     * Put data to specified backend endpoint
     *
     * @param {string} path
     * @param {object} parameters
     *
     * @returns {Promise<Response>}
     */
    put(path, parameters) {
        let url = this.getEndpoint() + path + this.getQuerystringFromParameters(parameters)
        let requestProperties = this.getRequestPropertiesForMethod('PUT', parameters)

        if (!requestProperties.headers) {
            requestProperties['headers'] = {}
        }

        const uuid = parameters.uuid
        if (uuid) {
            const eTag = this.getEtag(uuid)
            if (eTag) {
                requestProperties.headers['If-Match'] = eTag
            } else {
                console.warn('PUT request without ETag is deprecated')
            }
        } else {
            console.error('Bad PUT request. Missing uuid in parameters. Path was', path)
        }

        return fetch(url, requestProperties)

    }

    /**
     * Get data from specified backend endpoint
     *
     *
     *
     * @param {string} path
     * @param {object} parameters - Could contain headers that will be passed along parameters.headers
     *
     * @returns {Promise<Response>}
     *
     * @example
     * import {api} from 'writer'
     *
     * api.router.get('/api/binary/url/' + uuid)
     *     .done(function (url) {
     *         // Handle url
     *     }.bind(this))
     *     .error(function (error, xhr, text) {
     *         console.error(error, xhr, text);
     *     }.bind(this));
     */
    get(path, parameters) {
        let url = this.getEndpoint() + path + this.getQuerystringFromParameters(parameters)
        let requestProperties = this.getRequestPropertiesForMethod('GET', parameters)

        return fetch(url, requestProperties)
    }

    /**
     * Make a DELETE call to a specified backend endpoint
     *
     * @param {string} path - Backend endpoint
     * @param {object} parameters - Key value request parameters
     *
     * @returns {Promise<Response>}
     */
    del(path, parameters) {
        let url = this.getEndpoint() + path + this.getQuerystringFromParameters(parameters)
        let requestProperties = this.getRequestPropertiesForMethod('DELETE', parameters)

        return fetch(url, requestProperties)
    }


    /**
     * Return api backend url
     * @todo Specify api endpoint in config file
     *
     * @return {string}
     * @private
     */
    getEndpoint() {
        const location = window.location

        let result = location.protocol + '//' + location.hostname

        if (location.port) {
            result += ':' + location.port
        }

        return result
    }


    /**
     * Proxy ajax call to external backend
     *
     * @param {string} path
     * @param {object} op
     *
     * @return {object} jQuery ajax object
     */
    proxy(path, op) {
        var url = this.getEndpoint()

        if (typeof(path) !== 'undefined') {
            url += path
        }

        return $.ajax({
            method: 'post',
            dataType: 'text',
            url: url,
            headers: {
                'Content-Type': 'application/json'
            },
            data: JSON.stringify(op)
        })
    }

    /**
     * Gets a newsItem of specified type from server.
     * @param uuid      Uuid for newsItem.
     * @param imType    Infomaker type, e.g. x-im/image.
     * @returns {Promise.<T>|*}
     * @private
     */
    _getItem(uuid, imType) {
        return this.get('/api/newsitem/' + uuid, {imType: imType, headers: {'Content-Type': 'text/xml'}}, null)
            .then(response => this.checkForOKStatus(response))
            .then(response => this.handleEtag(response, uuid))
            .then(response => response.text())
            .then(text => {
                const parser = new DOMParser()
                return parser.parseFromString(text, 'text/xml')
            })
            .catch(e => {
                return Promise.reject(e)
            })
    }

    /**
     * Fetch a ConceptItem from the backend
     * @param id The id of the concept
     * @param imType The x-im/type
     * @return {*}
     */
    getConceptItem(id, imType) {
        return this._getItem(id, imType)
    }

    /**
     * Fetch a NewsItem from the backend
     * @param id The id of the news item
     * @param imType The x-im/type
     * @return {*}
     */
    getNewsItem(id, imType) {
        return this._getItem(id, imType)
    }

    getRemoteETag(uuid, imType) {
        return this.get('/api/newsitem/' + uuid, {imType: imType, headers: {'Content-Type': 'text/xml'}}, null)
            .then(response => this.checkForOKStatus(response))
            .then(response => response.headers.get('etag'))
    }

    /**
     * Updates a Concept Item
     * @param id The ID of the Concept Item to update
     * @param concept The updated XML
     * @return A promise with no data
     */
    updateConceptItem(id, concept) {
        return this.put('/api/newsitem/' + id, {body: concept, uuid: id})
            .then(response => this.checkForOKStatus(response))
            .then(response => this.handleEtag(response, id))
            .catch(e => {
                return Promise.reject(e)
            })
    }

    /**
     * Creates a Concept Item
     * @param concept The concept to create
     * @return {*|Promise.<TResult>} containing the resulting UUID
     */
    createConceptItem(concept) {
        return this.post('/api/newsitem', {body: concept})
            .then(response => this.checkForOKStatus(response))
            .then(response => this.handleEtag(response, null))
            .then(response => response.text())
            .catch(e => {
                return Promise.reject(e)
            })
    }

    /**
     * Method checks for a status code between 200 and 299
     * Throws error if otherwise.
     *
     * Use for example when you want to reject a fetch promise
     *
     * @example
     * fetch(...)
     *     .then(response => checkForOKStatus(response))
     *     .then(response => toJson(response))
     *     .then(json => ...)
     *     .catch(error => ...)
     *
     * @param response
     * @returns {*}
     */
    checkForOKStatus(response) {
        if (response.status >= 200 && response.status < 300) {
            return response
            // TODO Editor Service starting from 4.0.0 returns 412 when conflicting. This means that 409 can be
            // removed below when Editor Service is upgraded to 4.0.0 for all customers.
        } else if ([409, 412].includes(response.status)) {
            console.warn('412 Precondition failed')
            return new Promise((resolve, reject) => {
                reject(new ConflictError(this.api.getLabel('error-human-readable-conflict')))
            })
        } else {
            console.warn('Not OK status: ' + response.status)
            return new Promise((resolve, reject) => {
                response.text()
                    .then(text => {
                        reject(new ServerError(text))
                    })
                    .catch(e => {
                        reject(e)
                    })
            })
        }
    }


    /**
     * Use native response.json() instead
     *
     * @deprecated 2018-10-31
     */
    toJson(response) {
        console.warn('api.router.toJson is deprecated, use native response.json() instead')
        return response.json()
    }

    handleEtag(response, uuid) {
        // If no uuid supplied, try to use "Location" header
        uuid = uuid || this.getLocationFromHeaders(response)

        if (!uuid) {
            console.warn('Trying to set ETag for nil uuid')
            return response
        }

        if (response.headers.get('etag')) {
            this.setEtag(uuid, response.headers.get('etag'))
        }
        else {
            console.warn('Missing ETag for document with uuid', uuid)
        }

        return response
    }

    getEtag(uuid) {
        return this.etags[uuid]
    }

    setEtag(uuid, eTag) {
        this.etags[uuid] = eTag
    }

    getLocationFromHeaders(response) {
        if (response.headers && response.headers.get('location')) {
            return response.headers.get('location')
        }
        return null
    }
}

export {Router}
